import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { Check, Globe } from "lucide-react";
import {
  isLocalizedRoute,
  LANGUAGE_STORAGE_KEY,
  LOCALE_META,
  LOCALES,
  localizePath,
  splitLocalePath,
  useLocale,
  useTranslation,
  type Locale,
} from "@thyra/landing-i18n";
import { FlagIcon } from "./FlagIcon";

type LanguageSwitcherProps = {
  /** Which way the popover opens. */
  placement?: "bottom" | "top";
  className?: string;
};

const PANEL_WIDTH = 272;
const PANEL_GAP = 8;

/** An explicit choice outranks browser detection on later visits. */
function rememberLocale(locale: Locale) {
  try {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, locale);
  } catch {
    // Storage can be unavailable (private mode); the URL still carries it.
  }
}

/**
 * Language links are plain anchors to each locale's own URL: crawlers can
 * follow them, and a full navigation loads that locale's prerendered page.
 */
function useLanguageLinks() {
  const { pathname } = useLocation();
  const current = useLocale();
  const { path } = splitLocalePath(pathname);
  // English-only pages (legal, 404) have no translation: go to that
  // language's home instead.
  const target = isLocalizedRoute(path) ? path : "/";
  return LOCALES.map((locale) => ({
    locale,
    href: localizePath(target, locale),
    active: locale === current,
    ...LOCALE_META[locale],
  }));
}

export function LanguageSwitcher({
  placement = "bottom",
  className = "",
}: LanguageSwitcherProps) {
  const { t } = useTranslation("common");
  const links = useLanguageLinks();
  const current = links.find((link) => link.active) ?? links[0]!;
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLElement>(null);
  const panelId = useId();
  const [position, setPosition] = useState<React.CSSProperties | null>(null);

  const isInside = (node: EventTarget | null) =>
    node instanceof Node &&
    (rootRef.current?.contains(node) || panelRef.current?.contains(node));

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!isInside(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  // The open panel is portalled to <body> and pinned to the button with
  // fixed coordinates, so no ancestor (the footer card clips its overflow,
  // the navbar has a backdrop filter) can cut it off or sit on top of it.
  useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      // clientWidth excludes the scrollbar, like fixed `right` does.
      const viewport = document.documentElement.clientWidth;
      const right = Math.max(
        PANEL_GAP,
        Math.min(viewport - rect.right, viewport - PANEL_WIDTH - PANEL_GAP),
      );
      setPosition(
        placement === "top"
          ? {
              right,
              bottom:
                document.documentElement.clientHeight - rect.top + PANEL_GAP,
            }
          : { right, top: rect.bottom + PANEL_GAP },
      );
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, placement]);

  const focusLink = (index: number) => {
    const anchors = panelRef.current?.querySelectorAll<HTMLAnchorElement>("a");
    if (!anchors?.length) return;
    anchors[(index + anchors.length) % anchors.length]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape" && open) {
      setOpen(false);
      buttonRef.current?.focus();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    event.preventDefault();
    const anchors = Array.from(
      panelRef.current?.querySelectorAll<HTMLAnchorElement>("a") ?? [],
    );
    const focused = anchors.indexOf(
      document.activeElement as HTMLAnchorElement,
    );
    const step = event.key === "ArrowDown" ? 1 : -1;
    if (!open) setOpen(true);
    // The panel mounts on open; focus once it is in the document.
    requestAnimationFrame(() =>
      requestAnimationFrame(() =>
        focusLink(focused === -1 ? (step === 1 ? 0 : -1) : focused + step),
      ),
    );
  };

  return (
    <div
      ref={rootRef}
      className={`relative ${className}`}
      onKeyDown={onKeyDown}
      onBlur={(event) => {
        if (!isInside(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={t("language.current", { language: current.label })}
        className={`group inline-flex h-9 w-9 items-center justify-center rounded-full border transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
          open
            ? "border-primary/30 bg-primary/10 text-primary"
            : "border-border bg-background/60 text-muted-foreground hover:border-primary/30 hover:text-foreground"
        }`}
      >
        <Globe
          className="h-4 w-4 transition-transform duration-500 ease-out group-hover:rotate-[30deg]"
          strokeWidth={1.75}
          aria-hidden
        />
      </button>

      {/* Plain hidden links so every locale URL is in the prerendered HTML
          for crawlers; the interactive panel below only exists while open. */}
      <ul hidden>
        {links.map((link) => (
          <li key={link.locale}>
            <a href={link.href} hrefLang={link.locale} lang={link.locale}>
              {link.label}
            </a>
          </li>
        ))}
      </ul>

      {open &&
        position &&
        createPortal(
          <nav
            ref={panelRef}
            id={panelId}
            aria-label={t("language.select")}
            style={{ ...position, width: PANEL_WIDTH }}
            className={`language-panel-enter fixed z-[60] rounded-2xl border border-border bg-background p-1.5 shadow-xl shadow-black/10 dark:shadow-black/40 ${
              placement === "top" ? "origin-bottom-right" : "origin-top-right"
            }`}
          >
            <p className="px-3 pb-1.5 pt-2 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              {t("language.select")}
            </p>
            <ul className="flex flex-col gap-0.5">
              {links.map((link) => (
                <li key={link.locale}>
                  <a
                    href={link.href}
                    hrefLang={link.locale}
                    lang={link.locale}
                    aria-current={link.active ? "true" : undefined}
                    onClick={() => rememberLocale(link.locale)}
                    className={`flex h-10 items-center gap-3 rounded-xl px-3 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
                      link.active
                        ? "bg-primary/10 font-semibold text-primary"
                        : "text-foreground/90 hover:bg-muted"
                    }`}
                  >
                    <FlagIcon locale={link.locale} />
                    <span className="flex-1 truncate">{link.label}</span>
                    {link.active ? (
                      <Check className="h-4 w-4 shrink-0" aria-hidden />
                    ) : (
                      <span className="text-[11px] font-medium tracking-wide text-muted-foreground">
                        {link.short}
                      </span>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          </nav>,
          document.body,
        )}
    </div>
  );
}
